import { StaffShiftData } from '../components/staff/SimpleShiftBoard';

// StaffShiftData型を再エクスポート（他のモジュールで使用可能にする）
export type { StaffShiftData };

/**
 * サロンボード互換の拡張シフトデータ型定義
 * 美容サロン業界の実運用に最適化
 */

// 予定タイプの定義
export type AppointmentType = 
  | 'customer'      // 顧客施術
  | 'break'         // 休憩
  | 'meeting'       // 会議・ミーティング
  | 'training'      // 研修・練習
  | 'cleaning'      // 清掃・準備
  | 'unavailable';  // その他利用不可

export type AppointmentPriority = 'high' | 'medium' | 'low';

/**
 * 個別予定 - 基本勤務時間内の具体的なスケジュール
 */
export interface Appointment {
  id: string;
  startTime: string;      // 'HH:mm'
  endTime: string;        // 'HH:mm'
  type: AppointmentType;  // 予定タイプ
  title: string;          // 予定タイトル（30文字制限）
  memo: string;           // メモ（100文字制限）
  priority: AppointmentPriority;
  customerName?: string;  // 顧客名（typeが'customer'の場合）
}

/**
 * 基本勤務時間 - 予約受付可能な時間帯
 */
export interface BaseWorkingHours {
  start: string;  // 'HH:mm'
  end: string;    // 'HH:mm'
}

/**
 * 空き時間情報
 */
export interface AvailabilityInfo {
  totalWorkingMinutes: number;    // 基本勤務時間（分）
  bookedMinutes: number;          // 予定で埋まっている時間（分）
  availableMinutes: number;       // 空き時間（分）
  availabilityStatus: 'available' | 'busy' | 'partially_available';
  freeTimeSlots: { start: string; end: string; }[]; // 空き時間スロット
}

export interface HolidayInfo {
  title: string;      // 休日タイトル（30文字制限）
  memo: string;       // 休日メモ（100文字制限）
}

export type ShiftType = 'work' | 'holiday';
export type PresetShiftType = 'all_day' | 'morning' | 'afternoon' | 'custom';

/**
 * 拡張シフトデータ - サロンボード互換機能
 * 基本勤務時間と個別予定の分離により実運用に最適化
 */
export interface ExtendedStaffShiftData extends Omit<StaffShiftData, 'workingHours'> {
  shiftType: ShiftType;
  
  // 出勤時の詳細情報
  workDetails?: {
    presetType: PresetShiftType;
    baseWorkingHours: BaseWorkingHours;  // 基本勤務時間（予約受付可能時間）
    appointments: Appointment[];          // 個別予定リスト
    availabilityInfo?: AvailabilityInfo; // 空き時間情報（自動計算）
    
    // 後方互換性のため旧構造も保持（廃止予定）
    schedules?: ShiftSchedule[];
    workingHours?: {
      start: string;
      end: string;
    } | null;
  };
  
  // 休日時の情報
  holidayInfo?: HolidayInfo;
}

/**
 * 旧ShiftSchedule型（後方互換性のため保持）
 * @deprecated 新しいAppointment型を使用してください
 */
export interface ShiftSchedule {
  id: string;
  startTime: string;  // 'HH:mm'
  endTime: string;    // 'HH:mm'
  title: string;      // 予定タイトル（30文字制限）
  memo: string;       // メモ（100文字制限）
}

/**
 * 予定タイプ別の設定（アイコン、色、表示名）
 */
export const APPOINTMENT_CONFIG = {
  customer: {
    label: '施術',
    icon: '💇‍♀️',
    color: '#1976d2',      // blue
    bgColor: '#e3f2fd',
    priority: 'high' as AppointmentPriority
  },
  break: {
    label: '休憩',
    icon: '🍽️',
    color: '#ed6c02',      // orange
    bgColor: '#fff3e0',
    priority: 'medium' as AppointmentPriority
  },
  meeting: {
    label: '会議',
    icon: '📋',
    color: '#9c27b0',      // purple
    bgColor: '#f3e5f5',
    priority: 'medium' as AppointmentPriority
  },
  training: {
    label: '研修',
    icon: '📚',
    color: '#2e7d32',      // green
    bgColor: '#e8f5e8',
    priority: 'low' as AppointmentPriority
  },
  cleaning: {
    label: '清掃',
    icon: '🧹',
    color: '#795548',      // brown
    bgColor: '#efebe9',
    priority: 'low' as AppointmentPriority
  },
  unavailable: {
    label: '利用不可',
    icon: '🚫',
    color: '#d32f2f',      // red
    bgColor: '#ffebee',
    priority: 'high' as AppointmentPriority
  }
} as const;

/**
 * プリセットシフト定義（新構造対応）
 */
export const PRESET_SHIFTS = {
  all_day: {
    label: '全日',
    baseWorkingHours: { start: '09:00', end: '18:00' },
    defaultAppointments: []  // 基本勤務時間のみ設定、予定は空
  },
  morning: {
    label: '午前',
    baseWorkingHours: { start: '09:00', end: '13:00' },
    defaultAppointments: []
  },
  afternoon: {
    label: '午後',
    baseWorkingHours: { start: '14:00', end: '18:00' },
    defaultAppointments: []
  },
  custom: {
    label: 'カスタム',
    baseWorkingHours: { start: '09:00', end: '18:00' },
    defaultAppointments: []
  }
} as const;

/**
 * 空き時間計算ユーティリティ
 */
export class AvailabilityCalculator {
  /**
   * 時刻文字列を分に変換
   */
  static timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * 分を時刻文字列に変換
   */
  static minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  /**
   * 空き時間情報を計算
   */
  static calculateAvailability(
    baseWorkingHours: BaseWorkingHours,
    appointments: Appointment[]
  ): AvailabilityInfo {
    const startMinutes = this.timeToMinutes(baseWorkingHours.start);
    const endMinutes = this.timeToMinutes(baseWorkingHours.end);
    const totalWorkingMinutes = endMinutes - startMinutes;

    // 予定をソートして重複をチェック
    const sortedAppointments = appointments
      .map(apt => ({
        ...apt,
        startMinutes: this.timeToMinutes(apt.startTime),
        endMinutes: this.timeToMinutes(apt.endTime)
      }))
      .sort((a, b) => a.startMinutes - b.startMinutes);

    // 予定で埋まっている時間を計算
    let bookedMinutes = 0;
    let lastEndTime = startMinutes;
    const freeTimeSlots: { start: string; end: string; }[] = [];

    for (const appointment of sortedAppointments) {
      // 基本勤務時間内の予定のみを対象
      if (appointment.startMinutes >= startMinutes && appointment.endMinutes <= endMinutes) {
        // 前の予定との空き時間をチェック
        if (appointment.startMinutes > lastEndTime) {
          freeTimeSlots.push({
            start: this.minutesToTime(lastEndTime),
            end: this.minutesToTime(appointment.startMinutes)
          });
        }
        
        bookedMinutes += appointment.endMinutes - Math.max(appointment.startMinutes, lastEndTime);
        lastEndTime = Math.max(lastEndTime, appointment.endMinutes);
      }
    }

    // 最後の予定から勤務終了までの空き時間
    if (lastEndTime < endMinutes) {
      freeTimeSlots.push({
        start: this.minutesToTime(lastEndTime),
        end: this.minutesToTime(endMinutes)
      });
    }

    const availableMinutes = totalWorkingMinutes - bookedMinutes;
    
    // 空き状況の判定
    let availabilityStatus: 'available' | 'busy' | 'partially_available';
    if (bookedMinutes === 0) {
      availabilityStatus = 'available';
    } else if (availableMinutes === 0) {
      availabilityStatus = 'busy';
    } else {
      availabilityStatus = 'partially_available';
    }

    return {
      totalWorkingMinutes,
      bookedMinutes,
      availableMinutes,
      availabilityStatus,
      freeTimeSlots
    };
  }

  /**
   * 予定タイプ別の件数を集計
   */
  static summarizeAppointments(appointments: Appointment[]): Record<AppointmentType, number> {
    const summary: Record<AppointmentType, number> = {
      customer: 0,
      break: 0,
      meeting: 0,
      training: 0,
      cleaning: 0,
      unavailable: 0
    };

    appointments.forEach(apt => {
      summary[apt.type]++;
    });

    return summary;
  }
}

/**
 * 時刻選択オプション（5分刻み）
 */
export const generateTimeOptions = (start = 8, end = 22) => {
  const options: string[] = [];
  for (let hour = start; hour <= end; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(timeStr);
    }
  }
  return options;
};

export const TIME_OPTIONS = generateTimeOptions(8, 22);

/**
 * データ変換ユーティリティ（新構造対応）
 */
export const convertToExtended = (basic: StaffShiftData): ExtendedStaffShiftData => {
  if (basic.workingHours) {
    // 出勤データの場合 - 新しい構造で変換
    const baseWorkingHours: BaseWorkingHours = {
      start: basic.workingHours.start,
      end: basic.workingHours.end
    };
    
    const appointments: Appointment[] = []; // 初期は空の予定リスト
    const availabilityInfo = AvailabilityCalculator.calculateAvailability(baseWorkingHours, appointments);
    
    return {
      ...basic,
      shiftType: 'work',
      workDetails: {
        presetType: 'custom',
        baseWorkingHours,
        appointments,
        availabilityInfo,
        // 後方互換性のため旧構造も保持
        schedules: [{
          id: 'migrated',
          startTime: basic.workingHours.start,
          endTime: basic.workingHours.end,
          title: '通常勤務',
          memo: ''
        }],
        workingHours: basic.workingHours
      }
    };
  } else {
    // 休日データまたは未設定の場合
    return {
      ...basic,
      shiftType: basic.workingHours === null ? 'holiday' : 'work',
      holidayInfo: basic.workingHours === null ? {
        title: '休日',
        memo: ''
      } : undefined,
      workDetails: basic.workingHours === null ? undefined : {
        presetType: 'custom',
        baseWorkingHours: { start: '09:00', end: '18:00' },
        appointments: [],
        availabilityInfo: AvailabilityCalculator.calculateAvailability(
          { start: '09:00', end: '18:00' }, 
          []
        ),
        schedules: [],
        workingHours: null
      }
    };
  }
};

export const convertToBasic = (extended: ExtendedStaffShiftData): StaffShiftData => {
  let workingHours: { start: string; end: string } | null = null;
  
  if (extended.shiftType === 'work' && extended.workDetails?.baseWorkingHours) {
    // 新構造の基本勤務時間を使用
    workingHours = {
      start: extended.workDetails.baseWorkingHours.start,
      end: extended.workDetails.baseWorkingHours.end
    };
  } else if (extended.shiftType === 'work' && extended.workDetails?.schedules?.length) {
    // 後方互換性：旧構造のスケジュールから変換
    const firstSchedule = extended.workDetails.schedules[0];
    workingHours = {
      start: firstSchedule.startTime,
      end: firstSchedule.endTime
    };
  } else if (extended.shiftType === 'holiday') {
    workingHours = null;
  }
  
  return {
    id: extended.id,
    staffId: extended.staffId,
    date: extended.date,
    workingHours,
    createdAt: extended.createdAt,
    updatedAt: extended.updatedAt
  };
};

/**
 * 拡張データの空き時間情報を更新
 */
export const updateAvailabilityInfo = (extendedShift: ExtendedStaffShiftData): ExtendedStaffShiftData => {
  if (extendedShift.shiftType === 'work' && extendedShift.workDetails?.baseWorkingHours) {
    const availabilityInfo = AvailabilityCalculator.calculateAvailability(
      extendedShift.workDetails.baseWorkingHours,
      extendedShift.workDetails.appointments || []
    );
    
    return {
      ...extendedShift,
      workDetails: {
        ...extendedShift.workDetails,
        availabilityInfo
      }
    };
  }
  
  return extendedShift;
};